import { useMutation, useQuery } from 'urql';

import { useDisplayContext } from '~src/context/DisplayContext';
import {
  CustomerInput,
  DataConsentInput,
  HeatPumpOfferInput,
  InsertAndSendOffersDocument,
  OfferSavingsDocument,
  OfferSavingsQueryVariables,
  OfferTypeSavingsDocument,
  SolarOfferInput,
} from '~src/graphql';
import { useGoogleClickID } from '~src/hooks/useGoogleClickID';
import { useUTMFields } from '~src/hooks/useUTMFields';
import { AddressInput, DeepPartial, OfferProducts, ProductTypes, ResidenceInput } from '~src/model';
import {
  DeepNullToUndefined,
  deepNullToUndefined,
  hasMinimumRequiredProductsForSavingsCalculations,
  isResidenceFilled,
} from '~src/util';

import { useRegisterLead } from '../useLead';
import { formatSendOfferProducts, toAddressInput, toAddressInputWithFallback, toResidenceInput } from './mapper';

type OfferSavingsVariables = Omit<OfferSavingsQueryVariables, 'address' | 'residence' | 'offer'> & {
  offer: DeepPartial<OfferProducts>;
  address: Partial<OfferSavingsQueryVariables['address']>;
  residence: DeepPartial<Omit<ResidenceInput, 'address' | 'customer'>>;
};

export type InsertAndSendOffersVariables = {
  heatPumpOffers?: Array<HeatPumpOfferInput>;
  solarOffers?: Array<SolarOfferInput>;
  customer: CustomerInput;
  residence: ResidenceInput;
  address: AddressInput;
  dataConsent?: DataConsentInput;
};

export const useInsertAndSendOffers = () => {
  const { leadOrigin } = useDisplayContext();
  const { registerLead } = useRegisterLead();

  const googleClickId = useGoogleClickID();
  const utmFields = useUTMFields();

  const [{ fetching }, insertAndSendOffers] = useMutation(InsertAndSendOffersDocument);

  const insertOffers = async (
    variables: DeepNullToUndefined<InsertAndSendOffersVariables>,
    context?: Parameters<typeof insertAndSendOffers>[1]
  ) => {
    const { address, heatPumpOffers, solarOffers } = variables ?? {};

    const customer = {
      ...variables?.customer,
      googleClickId,
      utmFields,
      leadOrigin,
    };

    const hasOffer = heatPumpOffers?.length || solarOffers?.length;

    if (!address || !hasOffer) {
      await registerLead({ customer, address: toAddressInputWithFallback(address) });

      return;
    }

    return insertAndSendOffers({ ...variables, customer }, context);
  };

  return { fetching, insertOffers };
};

export const useCalculateOfferTypeSavings = (
  inputAddress?: DeepPartial<AddressInput>,
  productTypes: ProductTypes = ['heatPump', 'solar']
) => {
  const address = toAddressInput(inputAddress);

  const [result] = useQuery({
    query: OfferTypeSavingsDocument,
    variables: {
      address: address!,
      types: productTypes,
    },
    pause: !address || !productTypes.length,
  });

  return result;
};

export const useCalculateOfferSavings = (
  variables: OfferSavingsVariables,
  selectedProductTypes: ProductTypes = ['heatPump', 'solar']
) => {
  const { address: inputAddress, residence: inputResidence, offer, ...rest } = variables;

  const hasMinimumRequiredProducts = hasMinimumRequiredProductsForSavingsCalculations(offer);
  const isResidenceValid = isResidenceFilled(deepNullToUndefined(inputResidence));
  const address = toAddressInput(deepNullToUndefined(inputAddress));

  const [result] = useQuery({
    query: OfferSavingsDocument,
    variables: {
      address: address!,
      residence: toResidenceInput(inputResidence),
      offer: formatSendOfferProducts(offer ?? {}, selectedProductTypes),
      ...rest,
    },
    pause: !isResidenceValid || !address || !hasMinimumRequiredProducts,
  });

  return result;
};
